import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ClientPosition, DragState, useDragState} from './DragState';

/**
 * Forked from https://github.com/NeoRaider/react-multi-split-pane
 * MIT License
 */

const DEFAULT_MIN_SIZE = 50;
const DEFAULT_MAX_SIZE = -1; // -1 means no max size

// See SplitPane and HorizontalSplitPanel/VerticalSplitPanel for more information
export interface SplitPaneResizeOptions {
   split: 'horizontal' | 'vertical';
   className: string;
   resizerClassName?: string;

   children: React.ReactChild[];

   defaultSizes?: number[];
   defaultSizesIsPixelSize?: boolean[];

   minSize?: number | number[];
   maxSize?: number | number[];

   onDragStarted?: () => void;
   onChange?: (sizes: number[]) => void;
   onDragFinished?: (sizes: number[]) => void;
}

export interface ResizeState {
   index: number;
}

export interface SplitPaneResizeState {
   childPanes: {
      key: string;
      node: React.ReactNode;
      ref: React.RefObject<HTMLDivElement>;
      size: number;
      minSize: number;
      maxSize: number;
   }[];
   resizeState: ResizeState | null;
   handleDragStart: (index: number, pos: ClientPosition) => void;
}

export const useSplitPaneResize = (options: SplitPaneResizeOptions): SplitPaneResizeState => {

   const {
      children,
      split,
      defaultSizes,
      defaultSizesIsPixelSize,
      minSize: minSizes,
      maxSize: maxSizes,
      onDragStarted,
      onChange,
      onDragFinished
   } = options;

   const [sizes, setSizes] = useState(new Map<string, number>());
   const paneRefs = useRef(new Map<string, React.RefObject<HTMLDivElement>>());

   useMemo(()=>{
      setSizes(new Map<string,number>());
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [defaultSizes, defaultSizesIsPixelSize]);

   const getMovedSizes = useCallback(
      (dragState: DragState<ResizeState> | null): number[] => {
         const collectedSizes = children.map(
            (node, index) => {
               const nodeKey = getNodeKey(node, index);
               const size = sizes.get(nodeKey);
               return size || getInitialSize(index,
                                             defaultSizes,
                                             defaultSizesIsPixelSize,
                                             minSizes,
                                             maxSizes);
            }
         );

         if (dragState) {
            const {
               offset,
               extraState: { index },
            } = dragState;
            _moveResizer(collectedSizes, index, offset, minSizes, maxSizes);
         }

         return collectedSizes;
      },
      [children, defaultSizes, defaultSizesIsPixelSize, minSizes, maxSizes, sizes],
   );

   const handleDragFinished = useCallback(
      (dragState: DragState<ResizeState>) => {
         const movedSizes = getMovedSizes(dragState);

         setSizes(
            new Map(
               children.map((node, index): [string, number] => [
                  getNodeKey(node, index),
                  movedSizes[index],
               ]),
            ),
         );

         if (onDragFinished) {
            onDragFinished(movedSizes);
         }
      },
      [children, getMovedSizes, onDragFinished],
   );

   const [dragState, beginDrag] = useDragState<ResizeState>(split, handleDragFinished);
   const movedSizes = useMemo(() => getMovedSizes(dragState), [dragState, getMovedSizes]);
   const resizeState = dragState ? dragState.extraState : null;

   useEffect(() => {
      if (onChange && dragState) {
         onChange(movedSizes);
      }
   }, [dragState, movedSizes, onChange]);

   const childPanes: ChildPane[] = useMemo(() => {
      const prevPaneRefs = paneRefs.current;
      paneRefs.current = new Map<string, React.RefObject<HTMLDivElement>>();

      return children.map((node, index) => {
         const key = getNodeKey(node, index);

         const ref = prevPaneRefs.get(key) || React.createRef();
         paneRefs.current.set(key, ref);

         const minSize = getMinSize(index, minSizes);
         const maxSize = getMaxSize(index, maxSizes);

         return { key, node, ref, minSize, maxSize };
      });
   }, [children, minSizes, maxSizes]);

   const childPanesWithSizes = useMemo(
      () => {
         return childPanes.map((child, index) => {
            const size = movedSizes[index];
            return { ...child, size };
         });
      },
      [childPanes, movedSizes],
   );

   const handleDragStart = useCallback(
      (index: number, pos: ClientPosition): void => {
         const sizeAttr = split === 'vertical' ? 'width' : 'height';

         const clientSizes = new Map(
            childPanes.map(({ key, ref }): [string, number] => {
               const size = ref.current ? ref.current.getBoundingClientRect()[sizeAttr] : 0;
               return [key, size];
            }),
         );

         if (onDragStarted) {
            onDragStarted();
         }

         beginDrag(pos, { index });
         setSizes(clientSizes);
      },
      [beginDrag, childPanes, onDragStarted, split],
   );

   return { childPanes: childPanesWithSizes, resizeState, handleDragStart };
};

const getNodeKey = (node: React.ReactChild, index: number): string => {
   if (typeof node === 'object' && node && node.key != null) {
      return 'key.' + node.key;
   }

   return 'index.' + index;
};

const getMinSize = (index: number, minSizes?: number | number[]): number => {
   if (typeof minSizes === 'number') {
      return minSizes;
   }
   else if (minSizes instanceof Array) {
      return minSizes[index];
   }

   return DEFAULT_MIN_SIZE;
};

const getMaxSize = (index: number, maxSizes?: number | number[]): number => {
   if (typeof maxSizes === 'number') {
      if (maxSizes === -1 || maxSizes > 0) {
         return maxSizes;
      }
   }
   else if (maxSizes instanceof Array) {
      const value = maxSizes[index];
      if (value === -1 || value > 0) {
         return value;
      }
   }

   return DEFAULT_MAX_SIZE;
};

const getDefaultSize = (index: number, defaultSizes: number[] | undefined): number => {
   if (defaultSizes) {
      const value = defaultSizes[index];
      if (value >= 0 || value === -1) {
         return value;
      }
   }

   return 1;
};

const getInitialSize = (index: number, defaultSizes: number[] | undefined, defaultSizesIsPixelSize: boolean[] | undefined, minSizes: number | number[] | undefined, maxSizes: number | number[] | undefined): number => {
   const defaultSize = getDefaultSize(index, defaultSizes);

   if (defaultSizesIsPixelSize && !defaultSizesIsPixelSize[index]) {
      return defaultSize >= 0 ? defaultSize : 1;
   }

   if (defaultSize === -1) {
      return -1;
   }

   const minSize = getMinSize(index,minSizes);
   if (defaultSize < minSize) {
      return minSize;
   }

   const maxSize = getMaxSize(index,maxSizes);
   if (maxSize > 0 && defaultSize > maxSize) {
      return maxSize;
   }

   return defaultSize;
};

interface ChildPane {
   key: string;
   node: React.ReactChild;
   ref: React.RefObject<HTMLDivElement>;
   minSize: number;
   maxSize: number;
}

/**
 * When moving a resizer, this routine takes care of adjusting the sizes of the various
 * panes and their offsets (when you move them below their minimum you can push the entire
 * area into another region).
 *
 * @param sizes - The sizes of the various regions
 * @param index - An index pointing to the region being resized. index+1 will point to the other
 *                region affected by the resize.
 * @param offset - How much the resizer has moved in the positive or negative direction
 * @param minSizes - The minSizes of the various regions
 * @param maxSizes - The maxSizes of the various regions
 *
 * @return How much it pushes back
 */
export const _moveResizer = (sizes: number[],
                             index: number,
                             offset: number,
                             minSizes: number | number[] | undefined,
                             maxSizes: number | number[] | undefined) => {

   if (offset === 0) {
      return 0;
   }

   const availableOffset = computeAvailableOffset(sizes,index,offset,minSizes,maxSizes);

   if (availableOffset) {
      dispatchOffset(sizes,index,availableOffset,minSizes,maxSizes);
   }

   // Get rid of -0
   return availableOffset === 0 ? 0 : availableOffset;
};

const addOffset = (sizes: number[], index: number, offset: number) => {

   const size = sizes[index];

   sizes[index] = Math.max(size + offset, 0);
};

const dispatchStretch = (sizes: number[],
                         index: number,
                         offset: number,
                         maxSizes: number | number[] | undefined): number[] => {

   const sizesIndex = offset < 0 ? index + 1 : index;

   if (sizesIndex < 0 || sizesIndex > sizes.length-1) {
      return [];
   }

   const size = sizes[sizesIndex];

   const maxSize = getMaxSize(sizesIndex, maxSizes);

   const newSize = Math.min(maxSize > 0 ? maxSize : size + Math.abs(offset), size + Math.abs(offset));

   const dispatchedStretch = newSize - size;

   addOffset(sizes, sizesIndex, dispatchedStretch);

   if (dispatchedStretch < Math.abs(offset)) {

      const nextIndex = offset < 0 ? index + 1 : index - 1;

      const nextOffset = Math.sign(offset) * (Math.abs(offset) - dispatchedStretch);

      return [
         sizes[sizesIndex],
         ...dispatchStretch(sizes, nextIndex, nextOffset, maxSizes)
      ];
   }

   return [sizes[sizesIndex]];
};

const dispatchShrink = (sizes: number[],
                        index: number,
                        offset: number,
                        minSizes: number | number[] | undefined): number[] => {

   const sizesIndex = offset > 0 ? index + 1 : index;

   if (sizesIndex < 0 || sizesIndex > sizes.length-1) {
      return [];
   }

   const size = sizes[sizesIndex];

   const newSize = Math.max(getMinSize(sizesIndex, minSizes), size - Math.abs(offset));

   const dispatchedShrink = newSize - size;

   addOffset(sizes, sizesIndex, dispatchedShrink);

   if (Math.abs(dispatchedShrink) < Math.abs(offset)) {

      const nextIndex = offset > 0 ? index + 1 : index - 1;

      const nextOffset = Math.sign(offset) * (Math.abs(offset) + dispatchedShrink);

      return [
         sizes[sizesIndex],
         ...dispatchShrink(sizes, nextIndex, nextOffset, minSizes)
      ];
   }

   return [sizes[sizesIndex]];
};

const dispatchOffset = (sizes: number[],
                        index: number,
                        offset: number,
                        minSizes: number | number[] | undefined,
                        maxSizes: number | number[] | undefined) => {
   return [
      ...dispatchStretch(sizes, index, offset, maxSizes),
      ...dispatchShrink(sizes, index, offset, minSizes)
   ];
};

const checkPropagate = (sizes: number[], index: number, direction: number) => {
   if (direction > 0) {
      if (index < sizes.length - 1) {
         // In the future, we can add ability for some resizers to not move in response to others.
         // For now, we just always allow propagation.
         return true;
      }
   }
   else {
      if (index > 0) {
         // In the future, we can add ability for some resizers to not move in response to others.
         // For now, we just always allow propagation.
         return true;
      }
   }
   return false;
};

const computeAvailableStretch = (sizes: number[], index: number, offset: number, maxSizes: number | number[] | undefined): number => {

   const sizesIndex = offset < 0 ? index + 1 : index;

   if (sizesIndex < 0 || sizesIndex > sizes.length-1) {
      return 0;
   }

   const size = sizes[sizesIndex];

   const maxSize =  getMaxSize(sizesIndex, maxSizes);

   const availableStretch = maxSize > 0 ? maxSize - size : Math.abs(offset);

   if (availableStretch < Math.abs(offset)) {

      if (checkPropagate(sizes, index, -1 * offset)) {

         const nextOffset = Math.sign(offset) * (Math.abs(offset) - availableStretch);

         return availableStretch + computeAvailableStretch(sizes,
                                                           offset < 0 ? index + 1 : index - 1,
                                                           nextOffset,
                                                           maxSizes);
      }
   }

   return Math.min(availableStretch, Math.abs(offset));
};

const computeAvailableShrink = (sizes: number[], index: number, offset: number, minSizes: number | number[] | undefined): number => {

   const sizesIndex = offset > 0 ? index + 1 : index;

   if (sizesIndex < 0 || sizesIndex > sizes.length-1) {
      return 0;
   }

   const size = sizes[sizesIndex];

   const minSize =  Math.max(getMinSize(sizesIndex, minSizes), 0);

   const availableShrink = size - minSize;

   if (availableShrink < Math.abs(offset)) {

      if (checkPropagate(sizes, index, offset)) {

         const nextOffset = Math.sign(offset) * (Math.abs(offset) - availableShrink);

         return availableShrink + computeAvailableShrink(sizes,
                                                   offset > 0 ? index + 1 : index - 1,
                                                         nextOffset,
                                                         minSizes);
      }
   }

   return Math.min(availableShrink, Math.abs(offset));
};

const computeAvailableOffset = (sizes: number[], index: number, offset: number, minSizes: number | number[] | undefined , maxSizes: number | number[] | undefined) => {

   const stretch = computeAvailableStretch(sizes, index, offset, maxSizes);
   const shrink = computeAvailableShrink(sizes, index, offset, minSizes);

   return Math.min(stretch, shrink) * Math.sign(offset);
};
